import {
  expectArray,
  expectBoolean,
  expectRecord,
  expectString,
  parsePrepareStudentBillingCardInvoicesRequest,
  parseStudentBillingCurrency,
  parseStudentBillingDate,
  parseStudentBillingIdentifier,
  parseStudentBillingServiceMonth,
  STUDENT_BILLING_CUSTOMER_INVOICE_STATUSES,
  type PrepareStudentBillingCardInvoicesRequest,
  type StudentBillingCardInvoiceMonthDto
} from "@madamuh/domain";
import { apiGet, apiPostWithHeaders } from "./client";

const exact = (record: Record<string, unknown>, allowed: readonly string[], path: string) => {
  const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
  if (unknown.length) throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
};
const integer = (value: unknown, path: string, minimum = 0) => {
  if (!Number.isSafeInteger(value) || Number(value) < minimum) throw new Error(`${path} is invalid`);
  return Number(value);
};
const iso = (value: unknown, path: string) => {
  const parsed = expectString(value, path);
  if (!Number.isFinite(new Date(parsed).getTime())) throw new Error(`${path} is invalid`);
  return new Date(parsed).toISOString();
};
const root = (tenantSlug: string, serviceMonth: string) => `/api/v1/admin/tenants/${encodeURIComponent(tenantSlug)}/billing/months/${parseStudentBillingServiceMonth(serviceMonth)}/card-invoices`;

function parseMonth(value: unknown): StudentBillingCardInvoiceMonthDto {
  const record = expectRecord(value, "cardInvoiceMonth");
  exact(record, ["serviceMonth", "preparationVersion", "preparedAt", "stale", "blockingUnitCount", "blockingReason", "eligibleUnitCount", "capabilities", "invoices"], "cardInvoiceMonth");
  const capabilities = expectRecord(record.capabilities, "cardInvoiceMonth.capabilities");
  exact(capabilities, ["canReadCardInvoices", "canPrepareCardInvoices", "canExecuteCardInvoices"], "cardInvoiceMonth.capabilities");
  if (capabilities.canReadCardInvoices !== true) throw new Error("cardInvoiceMonth read capability is invalid");
  return {
    serviceMonth: parseStudentBillingServiceMonth(record.serviceMonth),
    preparationVersion: record.preparationVersion === null ? null : integer(record.preparationVersion, "cardInvoiceMonth.preparationVersion", 1),
    preparedAt: record.preparedAt === null ? null : iso(record.preparedAt, "cardInvoiceMonth.preparedAt"),
    stale: expectBoolean(record.stale, "cardInvoiceMonth.stale"),
    blockingUnitCount: integer(record.blockingUnitCount, "cardInvoiceMonth.blockingUnitCount"),
    blockingReason: record.blockingReason === null ? null : record.blockingReason === "feePolicyNotReady" ? "feePolicyNotReady" : (() => { throw new Error("cardInvoiceMonth.blockingReason is invalid"); })(),
    eligibleUnitCount: integer(record.eligibleUnitCount, "cardInvoiceMonth.eligibleUnitCount"),
    capabilities: {
      canReadCardInvoices: true,
      canPrepareCardInvoices: expectBoolean(capabilities.canPrepareCardInvoices, "cardInvoiceMonth.capabilities.canPrepareCardInvoices"),
      canExecuteCardInvoices: expectBoolean(capabilities.canExecuteCardInvoices, "cardInvoiceMonth.capabilities.canExecuteCardInvoices")
    },
    invoices: expectArray(record.invoices, (item, path) => {
      const invoice = expectRecord(item, path);
      exact(invoice, ["customerInvoiceId", "serviceMonth", "payerId", "payerSafeDisplayLabel", "scheduledChargeDate", "currency", "billingUnitCount", "studentCount", "grossAmount", "invoiceEligibleAmount", "feeRateBps", "applicationFeeWaived", "plannedApplicationFeeAmount", "status", "paymentMethodReady", "paymentAccountReady", "canExecute", "canRetry", "preparedAt"], path);
      if (!(STUDENT_BILLING_CUSTOMER_INVOICE_STATUSES as readonly unknown[]).includes(invoice.status)) throw new Error(`${path}.status is invalid`);
      return {
        customerInvoiceId: parseStudentBillingIdentifier(invoice.customerInvoiceId),
        serviceMonth: parseStudentBillingServiceMonth(invoice.serviceMonth),
        payerId: parseStudentBillingIdentifier(invoice.payerId),
        payerSafeDisplayLabel: expectString(invoice.payerSafeDisplayLabel, `${path}.payerSafeDisplayLabel`),
        scheduledChargeDate: parseStudentBillingDate(invoice.scheduledChargeDate),
        currency: parseStudentBillingCurrency(invoice.currency),
        billingUnitCount: integer(invoice.billingUnitCount, `${path}.billingUnitCount`, 1),
        studentCount: integer(invoice.studentCount, `${path}.studentCount`, 1),
        grossAmount: integer(invoice.grossAmount, `${path}.grossAmount`, 1),
        invoiceEligibleAmount: integer(invoice.invoiceEligibleAmount, `${path}.invoiceEligibleAmount`, 1),
        feeRateBps: integer(invoice.feeRateBps, `${path}.feeRateBps`, 1),
        applicationFeeWaived: expectBoolean(invoice.applicationFeeWaived, `${path}.applicationFeeWaived`),
        plannedApplicationFeeAmount: integer(invoice.plannedApplicationFeeAmount, `${path}.plannedApplicationFeeAmount`),
        status: invoice.status as StudentBillingCardInvoiceMonthDto["invoices"][number]["status"],
        paymentMethodReady: expectBoolean(invoice.paymentMethodReady, `${path}.paymentMethodReady`),
        paymentAccountReady: expectBoolean(invoice.paymentAccountReady, `${path}.paymentAccountReady`),
        canExecute: expectBoolean(invoice.canExecute, `${path}.canExecute`),
        canRetry: expectBoolean(invoice.canRetry, `${path}.canRetry`),
        preparedAt: iso(invoice.preparedAt, `${path}.preparedAt`)
      };
    }, "cardInvoiceMonth.invoices")
  };
}

export async function fetchStudentBillingCardInvoices(tenantSlug: string, serviceMonth: string) {
  return parseMonth(await apiGet(root(tenantSlug, serviceMonth)));
}

export async function prepareStudentBillingCardInvoices(tenantSlug: string, serviceMonth: string, request: PrepareStudentBillingCardInvoicesRequest) {
  const parsed = parsePrepareStudentBillingCardInvoicesRequest(request);
  const response = expectRecord(await apiPostWithHeaders(`${root(tenantSlug, serviceMonth)}/prepare`, parsed, { "Idempotency-Key": crypto.randomUUID() }), "cardInvoicePrepare");
  exact(response, ["replayed", "serviceMonth", "preparationVersion", "preparedAt", "stale", "blockingUnitCount", "blockingReason", "eligibleUnitCount", "capabilities", "invoices"], "cardInvoicePrepare");
  expectBoolean(response.replayed, "cardInvoicePrepare.replayed");
  return parseMonth(Object.fromEntries(Object.entries(response).filter(([key]) => key !== "replayed")));
}
